'use client'

import { Button, LuneTheme } from '@lune-fe/lune-ui-lib'
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { BlogpostSlicedDocument } from '@root/prismicio-types'
import moment from 'moment'
import { FC, useEffect, useState } from 'react'

import { BlogAuthor, getAuthor, getValidatedCustomLabel } from '@/app/(main)/resource-hub/utils'
import BlogCard from '@/components/BlogCard'
import { SubscribeToNewsletter } from '@/components/blogSlices/SubscribeToNewsletter'
import Subscribe from '@/components/Subscribe'
import { HUBSPOT_FORM_IDS } from '@/utills/constants'

const PAGE_SIZE = 12
const SECTION_SIZE = 6

// TODO @FESTINAJ: add a test
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
    const content = [...array]
    const arrays = []
    while (content.length) {
        arrays.push(content.splice(0, chunkSize))
    }
    return arrays
}

const SubscribeToNewsletterSection = () => {
    const { palette } = LuneTheme
    return (
        <Box
            sx={{
                width: '100%',
                borderTop: `solid 1px ${palette.Grey300}`,
                borderBottom: `solid 1px ${palette.Grey300}`,
            }}
        >
            <SubscribeToNewsletter noBorder noMargin />
        </Box>
    )
}

const BlogSection = ({
    blogs,
    handleBlogClick,
    authors,
}: {
    blogs: BlogpostSlicedDocument[]
    handleBlogClick: () => void
    authors: BlogAuthor[]
}) => {
    return (
        <>
            {blogs.map((article) => {
                return (
                    <BlogCard
                        size={'S'}
                        label={'Insight'}
                        customLabel={getValidatedCustomLabel(article.data.custom_label)}
                        sx={{
                            flexGrow: 1,
                            flexShrink: 1,
                            flexBasis: {
                                md: 'calc(33.33% - 12px)',
                                sm: 'calc(50% - 20px)',
                                xs: '100%',
                            },
                            maxWidth: {
                                md: 'calc(33.33% - 12px)',
                                sm: 'calc(50% - 20px)',
                                xs: '100%',
                            },
                        }}
                        key={`blog-card-${article.id}`}
                        title={article.data.title[0]!.text}
                        imageSrc={
                            'image' in article.data.body[0]!.primary &&
                            article.data.body[0]!.primary.image.url!
                                ? article.data.body[0]!.primary.image.url
                                : article.data.main_image.url!
                        }
                        date={moment(article.data.date, moment.ISO_8601, true)}
                        lastPublishedDate={article.last_publication_date}
                        href={`/blog/${article.uid}`}
                        onClick={handleBlogClick}
                        author={getAuthor(authors, article.data.author)}
                    />
                )
            })}
        </>
    )
}

const Section = ({
    content,
    handleBlogClick,
    authors,
}: {
    content: BlogpostSlicedDocument[]
    handleBlogClick: () => void
    authors: BlogAuthor[]
}) => {
    const sections = chunkArray(content, SECTION_SIZE)
    return (
        <>
            <BlogSection authors={authors} blogs={sections[0]} handleBlogClick={handleBlogClick} />
            {sections[1] && (
                <>
                    <SubscribeToNewsletterSection />
                    <BlogSection
                        authors={authors}
                        blogs={sections[1]}
                        handleBlogClick={handleBlogClick}
                    />
                </>
            )}
        </>
    )
}

const ResourceHub: FC<{ content: BlogpostSlicedDocument[]; authors: BlogAuthor[] }> = ({
    content,
    authors,
}) => {
    const [page, setPage] = useState<number>(1)

    useEffect(() => {
        const lastViewedPage = sessionStorage.getItem('lastViewedPage')
        if (lastViewedPage) {
            setPage(Number(lastViewedPage))
        }
    }, [])

    const saveLastViewedPage = () => {
        sessionStorage.setItem('lastViewedPage', page.toString())
    }

    return (
        <>
            <Stack spacing={9}>
                <SubscribeToNewsletterSection />
                <Box
                    sx={{
                        display: 'flex',
                        gap: { xs: 5, sm: 5, md: 2 },
                        rowGap: { xs: 5, sm: 5, md: 9 },
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                    }}
                >
                    {chunkArray(content.slice(0, page * PAGE_SIZE), PAGE_SIZE).map(
                        (content, index) => {
                            return (
                                <Section
                                    authors={authors}
                                    key={`section-${index}`}
                                    handleBlogClick={saveLastViewedPage}
                                    content={content}
                                />
                            )
                        },
                    )}
                </Box>
                {page * PAGE_SIZE < content.length && (
                    <Button
                        sx={{ alignSelf: 'center' }}
                        variant={'outlined'}
                        leftIcon={<ArrowDownwardOutlinedIcon />}
                        onClick={() => setPage((page) => page + 1)}
                    >
                        Load more
                    </Button>
                )}
            </Stack>
            <Subscribe hubspotFormId={HUBSPOT_FORM_IDS.blog} />
        </>
    )
}

export default ResourceHub
